/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Parts of the v2.1 tabs that the former tabs did not have: the fee calculator (Add-ons & phí),
 * the money figures and price / cost of a renewal (Tài chính), renewal and reminders (Vòng đời),
 * locks and transfer codes (Chuyển & bảo mật), the registry's last answer and the differences
 * with ours (Registry), the versions compared before a sync (Tuân thủ & đồng bộ) and the dossier's
 * staff notes (Lịch sử & ghi chú). Everything from the domain's own data.
 */

import { ArrowRight, Banknote, HandCoins, Receipt, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api, type DomainDetail } from '../../../api';
import type { DomainRegistrySync } from '../../../api/areas/core';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EmptyState, Loading } from '../../../components/States';
import { useWrite, WriteError } from '../../../components/Write';
import { formatDate, formatDateTime, formatMoney } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { OperationQuote } from '../../services/QuotePage';
import { useDomainPage } from '../context';
import { useNoticeLabel, useVocab } from '../refs';
import { DOSSIER, StatusBadge, VNNIC_SYNC } from '../status';
import { domainTabUrl } from './moved';
import { Kpi } from './OverviewTab';
import { EditAction } from './shared';
import { Facts } from './SubjectContactsTab';

const selectClass =
  'h-9 min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** Sums of amounts per currency, "1.200.000 ₫ · 10,00 US$". */
function sums(rows: readonly { amount: string; currency: string }[]): string {
  const by = new Map<string, number>();
  for (const r of rows) by.set(r.currency, (by.get(r.currency) ?? 0) + Number(r.amount));
  if (!by.size) return '—';
  return [...by].map(([currency, n]) => formatMoney({ amount: n.toFixed(2), currency })).join(' · ');
}

// ---------------------------------------------------------------------------
// Add-ons & phí

/** "Bảng tính phí theo nghiệp vụ": what an operation on this domain's TLD costs, line by line. */
export function FeeCalculator({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const [years, setYears] = useState(1);
  const [op, setOp] = useState('RENEW');
  const r = useApi((signal) => api.getTldPriceBreakdown(detail.domain.tld_id, { years }, { signal }), [detail.domain.tld_id, years]);
  const ops = r.status === 'success' ? r.data.operations : [];
  const shown = ops.find((o) => o.operation === op) ?? ops[0];
  return (
    <div id="fees" className="scroll-mt-20 space-y-3">
      <Section title={t('fees.title')} description={t('fees.hint')}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1 text-xs text-muted-foreground">
            {t('fees.operation')}
            <select className={selectClass} value={shown?.operation ?? op} onChange={(e) => setOp(e.target.value)} disabled={!ops.length}>
              {ops.map((o) => (
                <option key={o.operation} value={o.operation}>
                  {vocab('operation', o.operation)}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-xs text-muted-foreground">
            {t('fees.years')}
            <select className={selectClass} value={years} onChange={(e) => setYears(Number(e.target.value))}>
              {[1, 2, 3, 5, 10].map((y) => (
                <option key={y} value={y}>
                  {t('fees.yearsN', { count: y })}
                </option>
              ))}
            </select>
          </label>
        </div>
        {r.status === 'loading' ? <Loading rows={2} /> : null}
        {r.status === 'error' ? <DataError error={r.error} onRetry={r.reload} title={t('fees.failed')} /> : null}
      </Section>
      {shown ? <OperationQuote op={shown} /> : r.status === 'success' ? <EmptyState title={t('fees.none')} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tài chính

/** Invoiced, held and refunded for this domain, and a renewal's price against its registry cost. */
export function FinanceFigures({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const d = detail.domain;
  const commerce = useApi((signal) => api.getDomainCommerce(d.id, { signal }), [d.id]);
  const price = useApi((signal) => api.getTldPriceBreakdown(d.tld_id, { years: 1 }, { signal }), [d.tld_id]);
  const renew = price.status === 'success' ? price.data.operations.find((o) => o.operation === 'RENEW') : undefined;
  const c = commerce.status === 'success' ? commerce.data : null;
  const invoiced = c ? sums(c.invoices.map((i) => ({ amount: i.document_type === 'CREDIT_NOTE' ? `-${i.total}` : i.total, currency: i.currency }))) : '…';
  const held = c ? sums(c.holds.filter((h) => h.status === 'ACTIVE' || h.status === 'HELD').map((h) => ({ amount: h.amount, currency: h.currency }))) : '…';
  const refunded = c ? sums(c.refunds.map((r) => ({ amount: r.amount, currency: r.currency }))) : '…';
  const cost = renew?.registry_cost !== undefined && renew?.registry_cost !== null && renew ? Number(renew.registry_cost) : null;
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={Receipt} label={t('finance.invoiced')} value={invoiced} hint={c ? t('finance.invoices', { count: c.invoices.length }) : null} />
        <Kpi icon={HandCoins} label={t('finance.held')} value={held} hint={t('finance.heldHint')} />
        <Kpi icon={Banknote} label={t('finance.refunded')} value={refunded} hint={c ? t('finance.refunds', { count: c.refunds.length }) : null} />
      </div>
      <Section title={t('finance.priceTitle')} description={t('finance.priceHint')}>
        {renew && !renew.error ? (
          <Facts
            rows={[
              [t('finance.sell'), formatMoney({ amount: renew.subtotal, currency: renew.currency })],
              [t('finance.cost'), cost !== null ? formatMoney({ amount: cost.toFixed(2), currency: renew.currency }) : null],
              [t('finance.margin'), cost !== null ? formatMoney({ amount: (Number(renew.subtotal) - cost).toFixed(2), currency: renew.currency }) : null],
              [t('finance.total'), formatMoney({ amount: renew.total, currency: renew.currency })],
              [t('finance.autoRenew'), d.auto_renew ? t('finance.autoRenewOn', { count: d.auto_renew_period }) : t('finance.off')],
              [t('finance.paidThrough'), d.customer_expires_at ? formatDate(d.customer_expires_at) : null],
            ]}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{renew?.error ?? t('finance.noPrice')}</p>
        )}
        <Callout className="mt-5">{t('finance.note')}</Callout>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Vòng đời

/** Auto-renew and the expiry notices sent, with the renewal one click away. */
export function RenewalPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const notice = useNoticeLabel();
  const d = detail.domain;
  const r = useApi((signal) => api.getDomainTransfers(d.id, { signal }), [d.id]);
  const notices = r.status === 'success' ? [...r.data.expiry_notices].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)) : [];
  return (
    <Section title={t('renewal.title')} description={t('renewal.hint')} actions={<EditAction detail={detail} action="renew" label={t('renewal.renew')} />}>
      <Facts
        rows={[
          [t('renewal.autoRenew'), <Badge key="a" tone={d.auto_renew ? 'ok' : 'neutral'} icon={false}>{d.auto_renew ? t('renewal.on', { count: d.auto_renew_period }) : t('renewal.off')}</Badge>],
          [t('renewal.registryExpiry'), d.registry_expires_at ? formatDateTime(d.registry_expires_at) : null],
          [t('renewal.paidThrough'), d.customer_expires_at ? formatDateTime(d.customer_expires_at) : null],
          [t('renewal.lastNotice'), notices[0] ? `${notice(notices[0].notice_type)} · ${formatDateTime(notices[0].created_at)}` : t('renewal.noNotice')],
        ]}
      />
      {r.status === 'error' ? <DataError error={r.error} onRetry={r.reload} title={t('renewal.failed')} /> : null}
      {notices.length ? (
        <ul className="mt-4 grid gap-1.5 text-sm">
          {notices.slice(0, 6).map((n) => (
            <li key={n.id} className="flex flex-wrap gap-x-3 text-muted-foreground">
              <span className="font-medium text-foreground">{notice(n.notice_type)}</span>
              <span>{formatDateTime(n.created_at)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <Callout className="mt-5">{t('renewal.note')}</Callout>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Chuyển & bảo mật

/** Locks and transfer conditions: transfer lock, transfer code, legal locks, pending transfer. */
export function SecurityPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const d = detail.domain;
  const locked = detail.registry_statuses.some((s) => s.status === 'clientTransferProhibited');
  const legal = detail.locks.filter((l) => l.lock_type !== 'REGISTRAR_LOCK' && l.lock_type !== 'TRANSFER_LOCK');
  const transferLocks = detail.locks.filter((l) => l.lock_type === 'TRANSFER_LOCK' || l.lock_type === 'REGISTRAR_LOCK');
  return (
    <Section
      title={t('security.title')}
      description={t('security.hint')}
      actions={
        <>
          <EditAction detail={detail} action="lock" label={locked ? t('security.unlock') : t('security.lock')} />
          <EditAction detail={detail} action="transferCode" label={t('security.code')} />
        </>
      }
    >
      <Facts
        rows={[
          [t('security.transferLock'), <Badge key="l" tone={locked ? 'ok' : 'neutral'} icon={false}>{locked ? t('security.on') : t('security.off')}</Badge>],
          [t('security.ourLocks'), transferLocks.length ? transferLocks.map((l) => `${vocab('lock', l.lock_type)}${l.auto_release_at ? ` · ${t('security.until', { date: formatDate(l.auto_release_at) })}` : ''}`).join('; ') : t('security.none')],
          [t('security.code'), d.auth_info_set_at ? t('security.codeSet', { date: formatDateTime(d.auth_info_set_at) }) : t('security.noCode')],
          [t('security.legal'), legal.length ? <Badge key="g" tone="warn">{legal.map((l) => vocab('lock', l.lock_type)).join(', ')}</Badge> : t('security.none')],
          [t('security.transfer'), vocab('transferState', d.transfer_state)],
          [t('security.registrant'), <EditAction key="r" detail={detail} action="registrant" label={t('security.changeRegistrant')} />],
        ]}
      />
      <Callout className="mt-5" title={t('security.noteTitle')}>
        {t('security.note')}
      </Callout>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Registry

type Match = 'same' | 'differs' | 'unknown';

/** Name servers with addresses as sorted `host: a, b` lines, addresses lower-cased and sorted. */
const glueText = (list: { host: string; addresses: string[] }[]) =>
  list
    .filter((n) => n.addresses.length > 0)
    .map((n) => `${n.host.toLowerCase()}: ${n.addresses.map((a) => a.toLowerCase()).sort().join(', ')}`)
    .sort();

/** The last "Đồng bộ với registry" answer per domain, kept across the page reload it triggers. */
const lastSync = new Map<string, DomainRegistrySync>();

/**
 * "Registry & nhà đăng ký tài trợ" and "Chênh lệch nội bộ ↔ registry". "Đồng bộ với registry"
 * reads the domain at its registry now (shown to who may read the reconciliation, the same
 * permission), then reloads the page so "Đọc lúc", the table and the issues show what it found.
 */
export function RegistryStatePanels({ detail, reload }: { detail: DomainDetail; reload?: () => void }) {
  const { t } = useTranslation('domainPage');
  const { registryState, delegation } = useDomainPage();
  const d = detail.domain;
  const sync = useWrite((_: null, opts) => api.syncDomainRegistry(d.id, opts));
  const [synced, setSynced] = useState(() => lastSync.get(d.id) ?? null);
  const runSync = () =>
    void sync.run(null).then((r) => {
      if (!r) return;
      lastSync.set(d.id, r);
      setSynced(r);
      reload?.();
    });
  const syncedField = (f: string) =>
    f === 'registry_expires_at' ? t('registryState.expiry') : f === 'registry_statuses' ? t('registryState.statuses') : t(`registryState.issueTypes.${f}`, { defaultValue: f });
  const snap = registryState?.snapshot ?? null;
  const ours = {
    expiry: d.registry_expires_at ? formatDate(d.registry_expires_at) : '—',
    statuses: detail.registry_statuses.map((s) => s.status).sort(),
    ns: (delegation?.nameservers.map((n) => n.host.toLowerCase()) ?? []).sort(),
    glue: glueText(delegation?.nameservers ?? []),
  };
  const theirs = snap
    ? {
        expiry: snap.expires_at ? formatDate(snap.expires_at) : '—',
        statuses: [...(snap.statuses ?? [])].sort(),
        ns: (snap.nameservers ?? []).map((h) => h.toLowerCase()).sort(),
        // null: the registry's domain view does not carry addresses (host objects / REST), or an older snapshot.
        glue: snap.host_addrs ? glueText(snap.host_addrs) : null,
      }
    : null;
  const cmp = (a: unknown, b: unknown): Match => (theirs === null ? 'unknown' : JSON.stringify(a) === JSON.stringify(b) ? 'same' : 'differs');
  const rows = [
    { key: 'expiry', label: t('registryState.expiry'), ours: ours.expiry, theirs: theirs?.expiry ?? '—', match: cmp(ours.expiry, theirs?.expiry) },
    { key: 'statuses', label: t('registryState.statuses'), ours: ours.statuses.join(', ') || '—', theirs: theirs ? theirs.statuses.join(', ') || '—' : '—', match: cmp(ours.statuses, theirs?.statuses) },
    { key: 'ns', label: t('registryState.nameservers'), ours: ours.ns.join(', ') || '—', theirs: theirs?.ns.join(', ') || '—', match: delegation ? cmp(ours.ns, theirs?.ns) : ('unknown' as Match) },
    {
      key: 'glue',
      label: t('registryState.glue'),
      ours: ours.glue.join('; ') || '—',
      theirs: theirs?.glue?.join('; ') || '—',
      match: delegation && theirs?.glue ? cmp(ours.glue, theirs.glue) : ('unknown' as Match),
    },
  ];
  const issues = registryState?.issues ?? null;
  // "No difference" still shows when only the glue row is "Chưa đối chiếu" (a registry whose domain view carries no addresses).
  return (
    <>
      <Section
        title={t('registryState.title')}
        description={t('registryState.hint')}
        actions={
          issues !== null ? (
            <Button type="button" size="sm" variant="outline" disabled={sync.busy} onClick={runSync}>
              <RefreshCw className={cn(sync.busy && 'animate-spin')} /> {t(sync.busy ? 'registryState.sync.busy' : 'registryState.sync.button')}
            </Button>
          ) : null
        }
      >
        <Facts
          rows={[
            [t('registryState.registry'), d.registry_name],
            [t('registryState.sponsor'), snap?.sponsor ?? null],
            [t('registryState.roid'), snap?.roid ? <span key="r" className="font-mono">{snap.roid}</span> : null],
            [t('registryState.read'), snap ? formatDateTime(snap.captured_at) : t('registryState.notRead')],
            [t('registryState.created'), snap?.created_at ? formatDateTime(snap.created_at) : null],
            [t('registryState.connection'), d.registry],
          ]}
        />
        {sync.state.phase === 'error' ? (
          <div className="mt-3">
            <WriteError error={sync.state.error} />
          </div>
        ) : synced ? (
          <p role="status" className={cn('mt-3 text-xs', synced.outcome === 'ISSUES_OPENED' ? 'text-warning' : 'text-muted-foreground')}>
            {t('registryState.sync.done', { time: formatDateTime(synced.synced_at) })} {t(`registryState.sync.${synced.outcome}`, { fields: synced.fields.map(syncedField).join(', ') })}
          </p>
        ) : null}
      </Section>
      <Section id="differences" flush title={t('registryState.diffTitle')} description={t('registryState.diffHint')}>
        <DataTable
          columns={[
            { key: 'label', header: t('registryState.field'), cell: (r) => r.label },
            { key: 'ours', header: t('registryState.ours'), className: 'min-w-40 whitespace-normal', cell: (r) => <span className="text-sm">{r.ours}</span> },
            { key: 'theirs', header: t('registryState.theirs'), className: 'min-w-40 whitespace-normal', cell: (r) => <span className="text-sm">{r.theirs}</span> },
            {
              key: 'match',
              header: t('registryState.result'),
              cell: (r) => <Badge tone={r.match === 'same' ? 'ok' : r.match === 'differs' ? 'warn' : 'neutral'} icon={r.match !== 'unknown'}>{t(`registryState.match.${r.match}`)}</Badge>,
            },
          ]}
          rows={rows}
          rowKey={(r) => r.key}
        />
        {issues === null ? (
          <p className="px-5 py-4 text-xs text-muted-foreground">{t('registryState.issuesHidden')}</p>
        ) : issues.length ? (
          <ul className="grid gap-2 px-5 py-4">
            {issues.map((i) => {
              const open = i.state === 'OPEN' || i.state === 'AUTO_RECONCILING' || i.state === 'MANUAL_REVIEW';
              return (
                <li key={i.id} className={cn('rounded-lg border px-3 py-2 text-sm', open && 'border-warning/50 bg-warning-soft/40')}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{t(`registryState.issueTypes.${i.issue_type}`, { defaultValue: i.issue_type })}</span>
                    <Badge tone={open ? 'warn' : 'ok'}>{t(`registryState.issueStates.${i.state}`, { defaultValue: i.state })}</Badge>
                    <span className="text-xs text-muted-foreground">{t('registryState.detected', { date: formatDateTime(i.detected_at) })}</span>
                    <Link to={`/registry/reconciliation/${encodeURIComponent(i.id)}`} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                      {t('registryState.openIssue')} <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                  {i.resolution ? <div className="mt-1 text-xs text-muted-foreground">{i.resolution}</div> : null}
                </li>
              );
            })}
          </ul>
        ) : rows.every((r) => r.match === 'same' || (r.key === 'glue' && r.match === 'unknown')) ? (
          <p className="px-5 py-4 text-xs text-muted-foreground">{t('registryState.noIssues')}</p>
        ) : null}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tuân thủ & đồng bộ

/** "Đối chiếu phiên bản trước đồng bộ": the versions that must line up before a sync. */
export function SyncComparePanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const d = detail.domain;
  const x = detail.dossier;
  const vn = d.tld_class.startsWith('VN');
  const subject = useApi((signal) => (d.current_subject_id ? api.getSubject(d.current_subject_id, { signal }) : Promise.resolve(null)), [d.current_subject_id]);
  const vnnic = useApi((signal) => (vn ? api.getDomainVnnic(d.id, { signal }) : Promise.resolve(null)), [d.id, vn]);
  const current = subject.status === 'success' && subject.data ? (subject.data.versions?.find((v) => v.id === subject.data?.current_version_id)?.version_number ?? null) : null;
  const sync = vnnic.status === 'success' ? (vnnic.data?.sync ?? null) : null;
  const pinned = x?.subject_version_number ?? null;
  return (
    <Section title={t('syncCompare.title')} description={t('syncCompare.hint')}>
      <Facts
        rows={[
          [t('syncCompare.subject'), pinned !== null ? t('syncCompare.subjectValue', { pinned, current: current ?? '…' }) : null, pinned !== null && current !== null && pinned !== current ? t('syncCompare.behind') : false],
          [t('syncCompare.dossier'), x ? <span key="d" className="flex flex-wrap items-center gap-2">v{x.dossier_version} <StatusBadge map={DOSSIER} value={x.status} /></span> : null],
          vn ? [t('syncCompare.sent'), sync ? `v${sync.dossier_version}` : t('syncCompare.notSent'), x && sync && sync.dossier_version !== x.dossier_version ? t('syncCompare.notSentYet') : false] : null,
          vn ? [t('syncCompare.state'), sync ? <StatusBadge key="s" map={VNNIC_SYNC} value={sync.status} /> : null] : null,
          [t('syncCompare.compliance'), <Link key="c" to={domainTabUrl(d.ascii_name, 'compliance')} className="text-primary hover:underline">{t(`syncCompare.complianceStates.${d.compliance_state}`, { defaultValue: d.compliance_state })}</Link>],
        ]}
      />
      <Callout className="mt-5">{t('syncCompare.note')}</Callout>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Lịch sử & ghi chú

/** Staff notes on the domain's dossier (while it is in the review queue), newest first. */
export function DossierNotes({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const x = detail.dossier;
  const r = useApi((signal) => (x ? api.getDossierQueueItem(x.id, { signal }).then((v) => v, () => null) : Promise.resolve(null)), [x?.id]);
  const item = r.status === 'success' ? r.data : null;
  return (
    <Section
      id="notes"
      title={t('notes.title')}
      description={t('notes.hint')}
      actions={
        item && x ? (
          <Button asChild variant="outline" size="sm">
            <Link to={`/?tab=dossiers&open=${encodeURIComponent(x.id)}`}>{t('notes.add')}</Link>
          </Button>
        ) : null
      }
    >
      {r.status === 'loading' ? <Loading rows={1} /> : null}
      {item?.notes.length ? (
        <ul className="grid gap-2">
          {item.notes.map((n) => (
            <li key={n.id} className="rounded-lg border-l-4 border-primary/40 bg-muted/50 px-3 py-2 text-sm">
              <p className="whitespace-pre-wrap">{n.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {n.created_by_name ?? '—'} · {formatDateTime(n.created_at)}
              </p>
            </li>
          ))}
        </ul>
      ) : r.status === 'success' ? (
        <p className="text-sm text-muted-foreground">{item ? t('notes.none') : t('notes.notQueued')}</p>
      ) : null}
    </Section>
  );
}
